/**
 * Settings Integrations Page
 *
 * Shows the enrollment status of platform-level connectors:
 *   - Microsoft (SharePoint): Multi-tenant app — "available" or "not_configured"
 *   - GitHub: GitHub App — shows per-tenant installations
 *
 * No per-tenant credential forms — credentials live in per-provider K8s Secrets
 * (`connector-{provider}-credentials`) managed by `setup-connector-apps.sh`.
 *
 * Path: /settings/integrations
 */

import { AppModal } from '@/components/ui/app-modal';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Trash2, CheckCircle, XCircle, AlertCircle, Github, ShieldCheck, Download } from 'lucide-react';
import {
  useEnrollmentStatus,
  useDeleteGitHubInstallation,
  getErrorMessage,
  type GitHubInstallation,
} from '@/features/datasources/hooks/use-oauth-apps';

// =============================================================================
// Main Page
// =============================================================================

export function SettingsIntegrationsPage() {
  const { data: status, isLoading, error } = useEnrollmentStatus();
  const [confirmDelete, setConfirmDelete] = useState<GitHubInstallation | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [consentMessage, setConsentMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const consentPopupRef = useRef<Window | null>(null);
  const returnTo = searchParams.get('returnTo');

  // Handle Microsoft admin consent callback (?admin_consent=True or ?error=...)
  useEffect(() => {
    const adminConsent = searchParams.get('admin_consent');
    const consentError = searchParams.get('error');
    if (adminConsent === 'True') {
      setConsentMessage({ type: 'success', text: 'Microsoft admin consent granted successfully.' });
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('admin_consent');
      nextParams.delete('error');
      nextParams.delete('error_description');
      setSearchParams(nextParams, { replace: true });
    } else if (consentError) {
      const description = searchParams.get('error_description') || consentError;
      setConsentMessage({ type: 'error', text: `Admin consent failed: ${description}` });
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('admin_consent');
      nextParams.delete('error');
      nextParams.delete('error_description');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleAdminConsentMessage = useCallback((event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    if (event.data?.type !== 'admin-consent-callback') return;

    const adminConsent = event.data?.admin_consent;
    const consentError = event.data?.error;

    if (adminConsent === 'True') {
      setConsentMessage({ type: 'success', text: 'Microsoft admin consent granted successfully.' });
    } else if (consentError) {
      setConsentMessage({ type: 'error', text: `Admin consent failed: ${consentError}` });
    } else {
      setConsentMessage({ type: 'error', text: 'Admin consent did not complete successfully.' });
    }

    if (consentPopupRef.current) {
      try {
        consentPopupRef.current.close();
      } catch {
        // Some browsers enforce COOP restrictions on popup state access.
      }
      consentPopupRef.current = null;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleAdminConsentMessage);
    return () => window.removeEventListener('message', handleAdminConsentMessage);
  }, [handleAdminConsentMessage]);

  const handleOpenAdminConsent = useCallback((consentUrl: string) => {
    const width = 720;
    const height = 820;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    consentPopupRef.current = window.open(
      consentUrl,
      'atlas-admin-consent',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    );

    if (!consentPopupRef.current) {
      setConsentMessage({
        type: 'error',
        text: 'Popup blocked. Allow popups for this site and try again.',
      });
    }
  }, []);

  return (
    <div className="p-6 max-w-3xl">
      {returnTo && (
        <div className="mb-4">
          <Link
            to={returnTo}
            className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/30"
          >
            <span aria-hidden="true">←</span>
            Volver al wizard
          </Link>
        </div>
      )}

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Integrations
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Platform connector status for external data sources.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300">
          Failed to load enrollment status: {getErrorMessage(error)}
        </div>
      )}

      {consentMessage && (
        <div className={`mb-4 rounded-md p-4 text-sm ${
          consentMessage.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
        }`}>
          {consentMessage.text}
          <button
            onClick={() => setConsentMessage(null)}
            className="ml-3 underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="animate-pulse h-28 bg-gray-100 dark:bg-gray-800 rounded-lg" />
          ))}
        </div>
      ) : status ? (
        <div className="space-y-4">
          {/* Microsoft Connector */}
          <MicrosoftCard
            status={status.microsoft}
            consentUrl={status.microsoft_consent_url}
            onOpenConsent={handleOpenAdminConsent}
          />

          {/* GitHub Connector */}
          <GitHubCard
            status={status.github}
            installUrl={status.github_install_url}
            installations={status.github_installations}
            onDeleteInstallation={setConfirmDelete}
          />
        </div>
      ) : null}

      {/* Delete Confirmation */}
      {confirmDelete && (
        <DeleteInstallationDialog
          installation={confirmDelete}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// =============================================================================
// Microsoft Card
// =============================================================================

function MicrosoftCard({
  status,
  consentUrl,
  onOpenConsent,
}: {
  status: string;
  consentUrl: string | null;
  onOpenConsent: (consentUrl: string) => void;
}) {
  const isAvailable = status === 'available';

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Microsoft (SharePoint)
            </h3>
            {isAvailable ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded-full">
                <CheckCircle size={12} /> Ready
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 px-2 py-0.5 rounded-full">
                <AlertCircle size={12} /> Not Configured
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {isAvailable
              ? 'Users can connect SharePoint sources directly. Grant admin consent to pre-approve for your entire organization.'
              : 'Platform administrator needs to register the Microsoft App. Run setup-connector-apps.sh.'}
          </p>
        </div>
      </div>

      {/* Admin Consent Action */}
      {isAvailable && consentUrl && (
        <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
          <button
            type="button"
            onClick={() => onOpenConsent(consentUrl)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
          >
            <ShieldCheck size={16} />
            Grant Admin Consent
          </button>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
            Optional — pre-approves SharePoint access for all users in your Azure AD tenant.
            Without this, users will see an individual consent prompt.
          </p>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// GitHub Card
// =============================================================================

interface GitHubCardProps {
  status: string;
  installUrl: string | null;
  installations: GitHubInstallation[];
  onDeleteInstallation: (installation: GitHubInstallation) => void;
}

function GitHubCard({ status, installUrl, installations, onDeleteInstallation }: GitHubCardProps) {
  const hasInstallations = installations.length > 0;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              GitHub
            </h3>
            {hasInstallations ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded-full">
                <CheckCircle size={12} /> Installed
              </span>
            ) : status === 'not_installed' ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                <XCircle size={12} /> Not Installed
              </span>
            ) : null}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Install the Klviar GitHub App in your GitHub organization to connect repositories as knowledge sources.
          </p>
        </div>
      </div>

      {/* Install Action */}
      {installUrl && (
        <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
          <a
            href={installUrl}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-md transition-colors"
          >
            <Download size={16} />
            Install GitHub App
          </a>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
            {hasInstallations
              ? 'Install in additional organizations or manage existing installations.'
              : 'Required — without installation, the platform cannot access repositories.'}
          </p>
        </div>
      )}

      {/* Installations Table */}
      {hasInstallations && (
        <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Installations ({installations.length})
          </h4>
          <div className="space-y-2">
            {installations.map((inst) => (
              <div
                key={inst.id}
                className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 rounded-md px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <Github size={16} className="text-gray-500 dark:text-gray-400" />
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {inst.account_login}
                    </span>
                    <span className="text-xs text-gray-400 ml-2">
                      {inst.account_type}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    inst.status === 'active'
                      ? 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30'
                      : 'text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700'
                  }`}>
                    {inst.status}
                  </span>
                  <button
                    onClick={() => onDeleteInstallation(inst)}
                    className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    title="Remove installation"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Delete Installation Confirmation
// =============================================================================

function DeleteInstallationDialog({
  installation,
  onClose,
}: {
  installation: GitHubInstallation;
  onClose: () => void;
}) {
  const deleteMutation = useDeleteGitHubInstallation();

  const handleDelete = () => {
    deleteMutation.mutate(installation.id, { onSuccess: onClose });
  };

  return (
    <AppModal onClose={onClose} testId="github-remove-modal">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Remove GitHub Installation
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Remove the installation record for <strong>{installation.account_login}</strong>.
          Data sources using this installation will stop syncing.
        </p>
        {deleteMutation.error && (
          <div className="mb-4 rounded-md bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
            {getErrorMessage(deleteMutation.error)}
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-md transition-colors"
          >
            {deleteMutation.isPending ? 'Removing...' : 'Remove'}
          </button>
        </div>
      </div>
    </AppModal>
  );
}